import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
  variant?: 'default' | 'compact' | 'featured';
}

export default function MovieCard({ movie, variant = 'default' }: MovieCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group block cursor-pointer"
      >
        <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200 mb-2">
          <img
            src={movie.posterUrl}
            alt={movie.title}
            title={`${movie.title} - 7777影院高清在线观看`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
            <span className="text-xs text-white font-medium bg-primary-500/90 px-2 py-1 rounded-md">
              立即观看
            </span>
          </div>
          {movie.rating && (
            <div className="absolute top-2 right-2 bg-accent-500 text-background-50 text-xs font-bold px-2 py-0.5 rounded-md">
              {movie.rating}
            </div>
          )}
        </div>
        <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
          {movie.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-0.5">
          {movie.year} · {movie.duration}
        </p>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group block cursor-pointer"
      >
        <div className="relative overflow-hidden rounded-lg aspect-[16/9] bg-background-200">
          <img
            src={movie.bannerUrl}
            alt={movie.title}
            title={`${movie.title} - 7777影院高清在线观看`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <div className="flex items-center gap-2 mb-2">
              {movie.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs bg-accent-500/80 text-background-50 px-2 py-0.5 rounded-full"
                >
                  {tag}
                </span>
              ))}
              <span className="text-xs bg-primary-500/80 text-background-50 px-2 py-0.5 rounded-full font-bold">
                {movie.rating} 分
              </span>
            </div>
            <h3 className="text-lg font-heading font-bold text-white group-hover:text-accent-400 transition-colors">
              {movie.title}
            </h3>
            <p className="text-sm text-white/70 mt-1 line-clamp-2">{movie.description}</p>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block cursor-pointer"
      data-product-shop
    >
      <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200 mb-3">
        <img
          src={movie.posterUrl}
          alt={movie.title}
          title={`${movie.title} - 7777影院高清在线观看`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <div className="flex items-center justify-center">
            <span className="text-sm text-white font-medium bg-primary-500/90 px-4 py-1.5 rounded-full flex items-center gap-1">
              <i className="ri-play-fill text-base"></i>
              立即观看
            </span>
          </div>
        </div>
        {movie.rating && (
          <div className="absolute top-2 right-2 bg-accent-500 text-background-50 text-xs font-bold px-2 py-0.5 rounded-md">
            {movie.rating}
          </div>
        )}
        {movie.episodes && (
          <div className="absolute top-2 left-2 bg-primary-500/90 text-background-50 text-xs px-2 py-0.5 rounded-md">
            {movie.episodes}
          </div>
        )}
      </div>
      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
        {movie.title}
      </h3>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-xs text-foreground-500">{movie.year}</span>
        <span className="text-xs text-foreground-400">·</span>
        <span className="text-xs text-foreground-500">{movie.duration}</span>
      </div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {movie.tags.map((tag) => (
          <span
            key={tag}
            className="text-xs bg-secondary-100 text-secondary-800 px-1.5 py-0.5 rounded"
          >
            {tag}
          </span>
        ))}
      </div>
    </Link>
  );
}